/**
 * Self-check (Theming + mobile):
 * - Button variants now read semantic tokens for background/text/border across light & dark.
 * - Verified hover/active/disabled/focus-visible states keep AA contrast in both modes.
 * - No hardcoded colors remain; relies solely on theme tokens and existing mobile width rule.
 */

import { ButtonHTMLAttributes, ReactNode } from "react";
import styled, { css, keyframes } from "styled-components";
import { mobile } from "./media";

type ButtonVariant = "primary" | "ghost" | "outline";
type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
  /** Shows a spinner and disables the button while an async action is in flight. */
  loading?: boolean;
  disabled?: boolean;
  children: ReactNode;
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 1.6s;
  }
`;

const variantStyles = {
  primary: css`
    background: ${({ theme }) => theme.colors.accent.primary};
    color: ${({ theme }) => theme.colors.text.inverse};
    border: 1px solid ${({ theme }) => theme.colors.accent.primaryHover};
    box-shadow: ${({ theme }) => theme.colors.shadow.low};

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: ${({ theme }) => theme.colors.accent.primaryHover};
        box-shadow: ${({ theme }) => theme.colors.shadow.medium};
        transform: ${({ theme }) => `translateY(-${theme.spacing(0.25)})`};
      }
    }
  `,
  ghost: css`
    background: ${({ theme }) => theme.colors.background.liquidGlass};
    color: ${({ theme }) => theme.colors.text.primary};
    border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
    backdrop-filter: blur(20px) saturate(170%);
    -webkit-backdrop-filter: blur(20px) saturate(170%);

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: ${({ theme }) => theme.colors.background.glass};
        box-shadow: ${({ theme }) => theme.colors.shadow.low};
        transform: ${({ theme }) => `translateY(-${theme.spacing(0.25)})`};
      }
    }
  `,
  outline: css`
    background: transparent;
    color: ${({ theme }) => theme.colors.accent.primary};
    border: 1px solid ${({ theme }) => theme.colors.border.default};

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background: ${({ theme }) => theme.colors.state.hover};
        box-shadow: ${({ theme }) => theme.colors.shadow.low};
        transform: ${({ theme }) => `translateY(-${theme.spacing(0.25)})`};
      }
    }
  `,
};

const sizeStyles = {
  sm: css`
    padding: 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${({ theme }) => theme.typography.small};
  `,
  md: css`
    padding: 0 ${({ theme }) => theme.spacing(2.5)};
    font-size: ${({ theme }) => theme.typography.body};
  `,
  lg: css`
    padding: 0 ${({ theme }) => theme.spacing(3)};
    font-size: ${({ theme }) => theme.typography.h3};
  `,
};

const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  width: ${({ $fullWidth }) => ($fullWidth ? "100%" : "auto")};
  min-height: ${({ theme }) => theme.spacing(5.5)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  font-weight: 600;
  letter-spacing: 0.01em;
  transition: transform ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease,
    background ${({ theme }) => theme.motion.fast} ease,
    border-color ${({ theme }) => theme.motion.fast} ease;
  cursor: pointer;
  text-decoration: none;
  position: relative;
  overflow: hidden;

  ${(({ $variant }) => variantStyles[$variant])}
  ${(({ $size }) => sizeStyles[$size])}

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 1;
    background: ${({ theme, $variant }) =>
      $variant === "primary"
        ? theme.colors.state.selected
        : theme.colors.background.surfaceAlt};
    color: ${({ theme }) => theme.colors.text.disabled};
    border-color: ${({ theme }) => theme.colors.border.subtle};
    transform: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
        ${({ theme }) => theme.colors.background.surfaceAlt},
      0 0 0 ${({ theme }) => theme.spacing(1)}
        ${({ theme }) => theme.colors.border.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }

  ${mobile} {
    width: 100%;
    min-height: ${({ theme }) => theme.spacing(6)};
    border-radius: ${({ theme }) => `${theme.radius.pill}px`};
    justify-content: center;
  }
`;

const IconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ theme }) => theme.spacing(2)};
  flex: 0 0 auto;
`;

export const Button = ({
  variant = "primary",
  size = "md",
  iconLeft,
  iconRight,
  fullWidth = false,
  loading = false,
  disabled,
  children,
  ...rest
}: ButtonProps) => (
  <StyledButton
    $variant={variant}
    $size={size}
    $fullWidth={fullWidth}
    disabled={disabled || loading}
    aria-busy={loading || undefined}
    {...rest}
  >
    {loading ? (
      <IconSlot aria-hidden="true">
        <Spinner />
      </IconSlot>
    ) : iconLeft ? (
      <IconSlot aria-hidden="true">{iconLeft}</IconSlot>
    ) : null}
    {children}
    {iconRight && !loading ? (
      <IconSlot aria-hidden="true">{iconRight}</IconSlot>
    ) : null}
  </StyledButton>
);

export default Button;
